import { Component } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'

import toast from 'lib/toast'
import authentication from 'api/authentication'
import CompanyListForm from 'components/authentication/CompanyListForm'
import BusyOverlay from 'components/common/BusyOverlay'
import { withAuthContext } from 'context/with-auth-context'
import { messageFromResponseError } from 'utils/strings'

class GoogleCallback extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    authState: PropTypes.object
  }

  state = {
    submitting: false,
    accessToken: '',
    nextPathname: '',
    companies: []
  }

  // When the component mounts, we make an initial request to the Gremlin API
  // to get a Gremlin credential using Google's access token. If the result is
  // a list of companies, we set the state and wait until the user selects a
  // company at which point we will invoke handleGoogleLogin again.
  async componentDidMount() {
    const { location, history } = this.props

    try {
      const { hash } = location

      // Parse the hash
      const {
        access_token: accessToken,
        state: googleState
      } = queryString.parse(hash)

      const state = { accessToken }

      // If there's a path to forward to after sign in, grab it from the URL
      if (googleState?.length) {
        state.nextPathname = JSON.parse(googleState).nextPathname
      }

      await new Promise(resolve => this.setState(state, resolve))
      await this.handleGoogleLogin()
    } catch (err) {
      this.setState({ submitting: false })
      this.handleError(err, 'Unable to authenticate using Google.')
      history.push('/')
    }
  }

  handleLogin = async companyName => {
    try {
      await this.handleGoogleLogin(companyName)
    } catch (err) {
      this.setState({ submitting: false })
      this.handleError(err, 'Unable to authenticate using Google.')
    }
  }

  // We don't wrap this function in a try/catch because we expect errors
  // to be caught at the caller
  handleGoogleLogin = async companyName => {
    const { history } = this.props
    const { accessToken, nextPathname } = this.state

    this.setState({ submitting: true })
    const credentials = await authentication.signinWithGoogle({
      accessToken,
      companyName
    })

    if (credentials && credentials.result === 'chooseCompany') {
      this.setState({
        waitingForCompany: true,
        submitting: false,
        companies: credentials.companies
      })
    } else {
      await this.props.authState.startSession(credentials)

      // Must use history.location.pathname directly because pathname may have
      // changed while waiting on the promise.
      if (nextPathname !== history.location.pathname) {
        history.push(nextPathname || '/')
      }
    }
  }

  handleBack = () => {
    this.props.history.push('/')
  }

  handleError = (err, defaultText) => {
    const errorText = messageFromResponseError(err, defaultText)

    toast({
      type: 'error',
      text: errorText,
      time: 10000,
      testKey: 'signin'
    })
  }

  render() {
    const { companies, submitting } = this.state

    return companies?.length ? (
      <CompanyListForm
        companies={companies}
        handleBack={this.handleBack}
        handleCompanySelection={companyName => this.handleLogin(companyName)}
        submitting={submitting}
      />
    ) : (
      <BusyOverlay shown={true} text="Authorizing" />
    )
  }
}

export default withAuthContext(GoogleCallback)
